// The day popover of the calendar: what is already planned that day, then the form to schedule a
// task at a time of that day (POST /api/tasks with dueAt) or, with RÉPÉTER, to start a routine
// from that day (POST /api/routines with schedule.start = the day). Department, agent (auto or
// chosen), time, model (advanced), text, repeat picker, "je valide avant", live preview line.
import { h, icon, iconButton, button, uid, setText } from '../../dom.js';
import { t, DAYS, DAYS_SHORT } from '../../i18n/fr.js';
import { formatDateTime } from '../../format.js';
import { can, approveLocked } from '../../store.js';
import { WEEK_ORDER, toggleDay, sortDays } from '../automations/schedule.js';
import { MODELS } from '../automations/model.js';
import { longDayLabel, startOfDay } from './grid.js';
import {
  REPEAT_KINDS, EVERY_HOURS_MAX, TASK_TEXT_MAX, schedulableDepartments, agentsOfDept, approveDefault, defaultDayForm,
  validateDayForm, dayTaskBody, dayRoutineBody, dayFormPreview,
} from './form.js';
import { eventChip } from './views.js';
import './strings.js';

function labelled(labelText, control, { className, hidden = false } = {}) {
  if (!control.id) control.id = uid('calf');
  return h('div', { class: ['cal-f', className], hidden },
    h('label', { class: 'cal-f-label', htmlFor: control.id }, labelText), control);
}

/**
 * Builds the popover content.
 * @param {object} ctx app context
 * @param {{dayMs: number, floorId: string, preferredDeptIds?: Iterable<string>, events: object[], idx: object,
 *   now: () => number, onEvent: (ev: object, el: HTMLElement) => void, onDone: (kind: 'task'|'routine', res: object) => void,
 *   onClose: () => void}} opts
 * @returns {{el: HTMLElement, initialFocus: HTMLElement|null}}
 */
export function createDayPanel(ctx, opts) {
  const { dayMs, idx } = opts;
  const state = ctx.store.get();
  const now = opts.now();
  const past = startOfDay(dayMs) < startOfDay(now);
  const depts = schedulableDepartments(state, opts.floorId);
  const titleId = uid('calday');

  const head = h('div', { class: 'cal-pop-head' },
    h('div', { class: 'cal-pop-titles' },
      h('p', { class: 'mono-label cal-pop-eyebrow' }, t('cal.form.eyebrow')),
      h('h3', { class: 'cal-pop-title', id: titleId }, longDayLabel(dayMs))),
    iconButton('close', t('cal.form.close'), { variant: 'ghost', size: 16, onClick: () => opts.onClose() }));

  const eventsBox = opts.events.length
    ? h('div', { class: 'cal-pop-events' },
      h('p', { class: 'mono-label cal-pop-sub' }, t('cal.day.events')),
      h('div', { class: 'cal-pop-evlist' }, opts.events.map((ev) => eventChip(ev, { idx, onOpen: opts.onEvent, tabbable: true }))))
    : null;

  if (past || !depts.length) {
    const note = past ? t('cal.form.pastOnly') : t('cal.form.readOnly');
    const el = h('div', { class: 'cal-pop-inner', role: 'group', 'aria-labelledby': titleId },
      head, eventsBox, eventsBox ? null : h('p', { class: 'muted cal-pop-note' }, t('cal.emptyDay')), h('p', { class: 'muted cal-pop-note' }, note));
    return { el, initialFocus: null };
  }

  const form = defaultDayForm({ state, dayMs, now, depts, preferredDeptIds: opts.preferredDeptIds || [] });
  let busy = false;

  // --- controls ------------------------------------------------------------------------------------
  const deptSelect = h('select', { class: 'input select input-sm cal-f-dept' });
  const byFloor = new Map();
  for (const d of depts) {
    if (!byFloor.has(d.floorId)) byFloor.set(d.floorId, { name: d.floorName, list: [] });
    byFloor.get(d.floorId).list.push(d);
  }
  for (const g of byFloor.values()) {
    const target = byFloor.size > 1 ? h('optgroup', { label: g.name }) : deptSelect;
    for (const d of g.list) target.appendChild(h('option', { value: d.id }, d.name));
    if (target !== deptSelect) deptSelect.appendChild(target);
  }
  deptSelect.value = form.deptId;

  const agentSelectEl = h('select', { class: 'input select input-sm cal-f-agent' });
  const timeInput = h('input', { type: 'time', class: 'input input-sm cal-f-time', step: 60, value: form.time, required: true });
  const modelList = uid('calmodels');
  const modelInput = h('input', { type: 'text', class: 'input input-sm', list: modelList, maxLength: 80, placeholder: t('cal.form.modelDefault'), autocomplete: 'off', spellcheck: false });
  const advanced = h('details', { class: 'cal-f-adv' },
    h('summary', { class: 'mono-label' }, t('cal.form.advanced')),
    labelled(t('cal.form.model'), modelInput),
    h('datalist', { id: modelList }, MODELS.map((m) => h('option', { value: m }))));

  const textInput = h('textarea', {
    class: 'input textarea cal-f-text', rows: 3, maxLength: TASK_TEXT_MAX, placeholder: t('cal.form.textPlaceholder'),
    'aria-label': t('cal.form.text'),
  });

  const repeatBtn = h('button', {
    type: 'button', class: 'cal-repeat', 'aria-pressed': 'false', title: t('cal.form.repeatAria'),
    onClick: () => { form.repeat = !form.repeat; sync(); },
  }, icon('repeat', { size: 14 }), h('span', {}, t('cal.form.repeat')));
  const kindSelect = h('select', { class: 'input select input-sm cal-f-kind', 'aria-label': t('cal.form.repeatKind') },
    REPEAT_KINDS.map((k) => h('option', { value: k }, t(`cal.form.kind.${k}`))));
  kindSelect.value = form.repeatKind;

  const dayBtns = new Map();
  const daysRow = h('div', { class: 'cal-f-days', role: 'group', 'aria-label': t('cal.form.days') });
  for (const d of WEEK_ORDER) {
    const b = h('button', {
      type: 'button', class: 'cal-f-day', 'aria-pressed': 'false', 'aria-label': DAYS[d], title: DAYS[d],
      onClick: () => { form.days = toggleDay(form.days, d); sync(); },
    }, DAYS_SHORT[d].replace('.', '').slice(0, 2));
    dayBtns.set(d, b);
    daysRow.appendChild(b);
  }
  const everyInput = h('input', { type: 'number', class: 'input input-sm cal-f-every', min: 1, max: EVERY_HOURS_MAX, step: 1, value: String(form.everyHours), inputmode: 'numeric' });
  const untilInput = h('input', { type: 'time', class: 'input input-sm cal-f-time', step: 60 });
  const weekdaysBox = h('input', { type: 'checkbox', class: 'cal-f-check' });
  const hourlyRow = h('div', { class: 'cal-f-row cal-f-hourly' },
    labelled(t('cal.form.every'), everyInput, { className: 'cal-f-inline' }),
    h('span', { class: 'muted' }, t('cal.form.everyUnit')),
    labelled(t('cal.form.until'), untilInput, { className: 'cal-f-inline' }),
    h('label', { class: 'cal-f-checklabel' }, weekdaysBox, t('cal.form.weekdaysOnly')));
  const repeatBox = h('div', { class: 'cal-f-repeatbox' }, daysRow, hourlyRow);

  const approveBox = h('input', { type: 'checkbox', class: 'cal-f-check', checked: form.approve });
  const approveHint = h('span', { class: 'cal-f-hint' });
  const approveLabel = h('label', { class: 'cal-f-checklabel cal-f-approve' }, approveBox, h('span', {}, t('cal.form.approve')));

  const preview = h('p', { class: 'cal-pop-preview', 'aria-live': 'polite' });
  const errorEl = h('p', { class: 'cal-pop-error', role: 'alert', hidden: true });
  const submitBtn = button(t('cal.form.submit'), { variant: 'primary', size: 'sm', type: 'submit', title: t('cal.form.submitTitle') });

  // --- state sync ----------------------------------------------------------------------------------
  let approveTouched = false;
  const currentDept = () => depts.find((d) => d.id === form.deptId) || null;
  const agentById = (id) => (ctx.store.get().office.agents || []).find((a) => a.id === id) || null;

  function fillAgents() {
    const keep = form.agentId;
    agentSelectEl.replaceChildren(h('option', { value: '' }, t('cal.form.agentAuto')));
    for (const a of agentsOfDept(ctx.store.get(), form.deptId)) {
      agentSelectEl.appendChild(h('option', { value: a.id }, `${a.lead ? '★ ' : ''}${a.name}${a.role ? ` — ${a.role}` : ''}`));
    }
    form.agentId = [...agentSelectEl.childNodes].some((o) => o.value === keep) ? keep : '';
    agentSelectEl.value = form.agentId;
  }

  function sync() {
    const canManage = !!currentDept() && can(ctx.store.get(), 'manage', { deptId: form.deptId });
    if (!canManage && form.repeat) form.repeat = false;
    repeatBtn.disabled = !canManage;
    repeatBtn.setAttribute('aria-pressed', String(form.repeat));
    repeatBtn.classList.toggle('is-on', form.repeat);
    repeatBtn.title = canManage ? t('cal.form.repeatAria') : t('cal.form.repeatNeedsManage');
    kindSelect.hidden = !form.repeat;
    repeatBox.hidden = !form.repeat || (form.repeatKind !== 'weekly' && form.repeatKind !== 'hourly');
    daysRow.hidden = form.repeatKind !== 'weekly';
    hourlyRow.hidden = form.repeatKind !== 'hourly';
    const days = new Set(sortDays(form.days || []));
    for (const [d, b] of dayBtns) {
      b.setAttribute('aria-pressed', String(days.has(d)));
      b.classList.toggle('is-on', days.has(d));
    }
    // Below manage the server refuses "auto" for an agent that asks for validation (SPEC §8); with
    // agent Auto the routed agent is unknown, so only an all-autonomous department allows it.
    const locked = !!currentDept() && approveLocked(ctx.store.get(), { deptId: form.deptId, agentId: form.agentId });
    approveBox.disabled = locked;
    if (locked) { form.approve = true; approveBox.checked = true; }
    const hintKey = locked ? 'cal.form.approveLocked' : form.approve ? 'cal.form.approveHint' : 'cal.form.autoHint';
    setText(approveHint, t(hintKey));
    approveLabel.title = t(hintKey);
    setText(submitBtn.querySelector('.btn-label') || submitBtn, form.repeat ? t('cal.form.submitRoutine') : t('cal.form.submit'));
    const pv = dayFormPreview(form, dayMs, { now: opts.now() });
    setText(preview, pv.text);
    preview.classList.toggle('is-error', !pv.ok);
  }

  function setError(msg) {
    setText(errorEl, msg || '');
    errorEl.hidden = !msg;
  }

  function readInputs() {
    form.deptId = deptSelect.value;
    form.agentId = agentSelectEl.value;
    form.time = timeInput.value ? timeInput.value.slice(0, 5) : '';
    form.model = modelInput.value;
    form.text = textInput.value;
    form.repeatKind = kindSelect.value;
    form.everyHours = Number.parseInt(everyInput.value, 10);
    form.until = untilInput.value ? untilInput.value.slice(0, 5) : '';
    form.weekdaysOnly = !!weekdaysBox.checked;
    form.approve = !!approveBox.checked;
  }

  const onInput = () => { readInputs(); setError(''); sync(); };
  for (const el of [timeInput, modelInput, textInput, kindSelect, everyInput, untilInput]) {
    el.addEventListener('input', onInput);
    el.addEventListener('change', onInput);
  }
  weekdaysBox.addEventListener('change', onInput);
  approveBox.addEventListener('change', () => { approveTouched = true; onInput(); });
  deptSelect.addEventListener('change', () => { readInputs(); fillAgents(); applyAgentDefault(); setError(''); sync(); });
  agentSelectEl.addEventListener('change', () => { readInputs(); applyAgentDefault(); sync(); });
  textInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
  });

  function applyAgentDefault() {
    if (approveTouched) return;
    form.approve = approveDefault(agentById(form.agentId));
    approveBox.checked = form.approve;
  }

  // --- submit --------------------------------------------------------------------------------------
  async function submit() {
    if (busy) return;
    readInputs();
    const current = opts.now();
    const errors = validateDayForm(form, { dayMs, now: current, state: ctx.store.get(), depts });
    const first = errors.form || errors.deptId || errors.text || errors.time || errors.schedule || errors.model;
    if (first) {
      setError(first);
      if (errors.text) textInput.focus();
      else if (errors.time) timeInput.focus();
      else if (errors.model) { advanced.open = true; modelInput.focus(); }
      return;
    }
    const dept = currentDept();
    busy = true;
    submitBtn.disabled = true;
    submitBtn.classList.add('is-busy');
    setError('');
    try {
      if (form.repeat) {
        const body = dayRoutineBody(form, dept, dayMs, ctx.store.get());
        const res = await ctx.api.post('/api/routines', body);
        const routine = res?.routine && typeof res.routine === 'object' ? res.routine : res;
        ctx.toast.success(t('cal.form.createdRoutine', { description: routine?.description || preview.textContent }));
        opts.onDone('routine', routine);
      } else {
        const body = dayTaskBody(form, dept, dayMs);
        const res = await ctx.api.post('/api/tasks', body);
        if (res && typeof res === 'object' && res.id && res.state) ctx.store.applyTask?.(res);
        ctx.toast.success(t('cal.form.createdTask', { when: formatDateTime(body.dueAt, opts.now()) }));
        opts.onDone('task', res);
      }
    } catch (err) {
      setError(err?.status === 404 ? t('cal.form.unavailable') : (err?.message || t('common.error')));
    } finally {
      busy = false;
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-busy');
    }
  }

  const el = h('form', {
    class: 'cal-pop-inner', novalidate: true, 'aria-labelledby': titleId,
    onSubmit: (e) => { e.preventDefault(); submit(); },
  },
  head,
  eventsBox,
  h('div', { class: 'cal-f-row' },
    labelled(t('cal.form.dept'), deptSelect, { className: 'cal-f-grow' }),
    labelled(t('cal.form.time'), timeInput)),
  labelled(t('cal.form.agent'), agentSelectEl),
  textInput,
  h('div', { class: 'cal-f-row cal-f-repeatrow' }, repeatBtn, kindSelect, approveLabel),
  repeatBox,
  approveHint,
  advanced,
  preview,
  errorEl,
  h('div', { class: 'cal-pop-foot' }, submitBtn));

  fillAgents();
  applyAgentDefault();
  sync();
  return { el, initialFocus: textInput };
}
